import React, { useCallback, useEffect, useState } from "react"
import { AlertButton, View } from "react-native"

import { Dialog as ThemedDialog } from "@rn-vui/themed"

import { Button, Text } from "src/components/Ignite"
import { useAppTheme } from "src/hooks/useAppTheme"

interface AlertState {
  isVisible: boolean
  title: string
  message?: string
  buttons: AlertButton[]
}

// Global alert manager
class AlertManager {
  private listeners: Array<(state: AlertState) => void> = []
  private currentState: AlertState = {
    isVisible: false,
    title: "",
    message: "",
    buttons: [],
  }

  subscribe(listener: (state: AlertState) => void) {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener)
    }
  }

  show(title: string, message?: string, buttons?: AlertButton[]) {
    this.currentState = {
      isVisible: true,
      title,
      message,
      buttons: buttons || [{ text: "OK" }],
    }
    this.notifyListeners()
  }

  hide() {
    this.currentState = { ...this.currentState, isVisible: false }
    this.notifyListeners()
  }

  getCurrentState() {
    return this.currentState
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener(this.currentState))
  }
}

const alertManager = new AlertManager()

// React component that renders the alert
export const AlertContainer: React.FC = () => {
  const { theme } = useAppTheme()
  const [alertState, setAlertState] = useState<AlertState>(alertManager.getCurrentState())

  useEffect(() => {
    const unsubscribe = alertManager.subscribe(setAlertState)
    return unsubscribe
  }, [])

  const handleButtonPress = useCallback((button: AlertButton) => {
    alertManager.hide()
    button.onPress?.()
  }, [])

  const handleBackdropPress = useCallback(() => {
    const cancelButton = alertState.buttons.find((button) => button.style === "cancel")
    if (cancelButton) {
      handleButtonPress(cancelButton)
    } else {
      alertManager.hide()
    }
  }, [alertState.buttons, handleButtonPress])

  return (
    <ThemedDialog
      isVisible={alertState.isVisible}
      onBackdropPress={handleBackdropPress}
      overlayStyle={{
        backgroundColor: theme.colors.background,
        borderRadius: 12,
        padding: 24,
        maxWidth: 360,
        width: "90%",
      }}
    >
      <ThemedDialog.Title
        title={alertState.title}
        titleStyle={{
          color: theme.colors.text,
          fontWeight: "600",
          fontSize: 20,
          marginBottom: 12,
        }}
      />
      {alertState.message && (
        <Text style={{ color: theme.colors.text, marginBottom: 24 }}>{alertState.message}</Text>
      )}
      <View style={{ gap: 12 }}>
        {alertState.buttons.map((button, index) => (
          <Button
            key={index}
            text={button.text || "OK"}
            preset="subtle"
            onPress={() => handleButtonPress(button)}
          />
        ))}
      </View>
    </ThemedDialog>
  )
}

export const Alert = {
  alert: (title: string, message?: string, buttons?: AlertButton[]) => {
    alertManager.show(title, message, buttons)
  },
  prompt: () => {
    throw new Error("Not implemented")
  },
}
